import { cfgCdnBanners, cfgHasSports, FeatureSet, ReleaseStage, stateFeatureSet, stateHasSports, stateTheme, stateUi, Theme, wantAtLeastReleaseStage } from '@infinitro/config';
import { stateDepositBonusOffers } from '@infinitro/global';
import { get } from 'svelte/store';
import { createDynamicCanonicalItems } from './dynamic';
import { createFlushCanonicalItems as createFlushCanonicalItemsFromFile } from './flush';
import { createFomoCanonicalItems as createFomoCanonicalItemsFromFile } from './fomo';
import { createLunaCanonicalItems } from './luna';
import { createRocketbetCanonicalItems as createRocketbetCanonicalItemsFromFile } from './rocket';
import { createTurboCanonicalItems } from './turbo';
const BANNERS_CDN = cfgCdnBanners();
const BANNERS_BASE = get(stateFeatureSet) === FeatureSet.Preview ? `${BANNERS_CDN}staging/` : `${BANNERS_CDN}`;
const stateUiSetting = get(stateUi);
const normalizePromotionDate = (date) => {
    if (!date)
        return null;
    return date instanceof Date ? date : new Date(date);
};
const normalizePromotionDates = (promotion) => ({
    ...promotion,
    startDate: normalizePromotionDate(promotion.startDate),
    endDate: normalizePromotionDate(promotion.endDate)
});
export const bannerSet = (id, isPNG = true) => ({
    banner: `${BANNERS_BASE}${id}/${id}.${isPNG ? 'png' : 'jpg'}`,
    bannerBig: `${BANNERS_BASE}${id}/${id}-detail.png`
});
const ONLY_IMAGE_DIMENSIONS = {
    desktopBannerSize: '1080x405',
    desktopBannerBigSize: '1400x350',
    mobileBannerSize: '1080x405',
    mobileBannerBigSize: '1080x405'
};
const createOnlyImagePromotion = (promotion) => ({
    noSignUp: true,
    onlyImage: true,
    autoBanner: true,
    ...ONLY_IMAGE_DIMENSIONS,
    ...promotion
});
const DEFAULT_DERIVED_TOP_SECTION_PROMOTION_START = new Date('2000-01-01T00:00:00Z');
const createPromotionFromTopSection = (canonicalId, topSection) => ({
    id: topSection.id ?? canonicalId,
    title: topSection.title ?? '',
    description: topSection.description,
    showTimezoneSuffixInFallback: topSection.showTimezoneSuffixInFallback,
    buttonText: topSection.buttonText,
    url: topSection.link,
    startDate: topSection.startDate ?? DEFAULT_DERIVED_TOP_SECTION_PROMOTION_START,
    endDate: topSection.endDate,
    rel: topSection.rel,
    banner: topSection.backgroundImage,
    customTitleWidth: topSection.customTitleWidth ?? undefined,
    amountBox: topSection.amountBox,
    conditionalUrl: topSection.conditionalLink
        ? {
            loggedInUrl: topSection.conditionalLink.loggedInLink,
            loggedOutUrl: topSection.conditionalLink.loggedOutLink
        }
        : undefined
});
const createFlushCanonicalItems = () => createFlushCanonicalItemsFromFile({
    removePromotionDuplicatesFromTopSectionSurface,
    createPromotionFromTopSection,
    bannersBase: BANNERS_BASE
});
const createFomoCanonicalItems = (locale, now) => createFomoCanonicalItemsFromFile(locale, now, {
    createOnlyImagePromotion,
    stateUiSetting,
    defaultDerivedTopSectionPromotionStart: DEFAULT_DERIVED_TOP_SECTION_PROMOTION_START
});
const createRocketbetCanonicalItems = () => createRocketbetCanonicalItemsFromFile();
const getPromotionsByTheme = (theme, locale, now) => {
    return getCanonicalThemeEntries(theme, locale, now)
        .filter((entry) => !!entry.promotion)
        .sort((a, b) => (a.order?.promotions ?? Number.MAX_SAFE_INTEGER) -
        (b.order?.promotions ?? Number.MAX_SAFE_INTEGER))
        .map((entry) => entry.promotion);
};
export const filterPromotionsByBusinessRules = (list, context) => {
    const { featureSet, isLoggedIn, hasSports, depositBonusOffersCount, showExpiredPromotions, now, theme } = context;
    return list.filter((promotion) => (!promotion.rel || promotion.rel === featureSet) &&
        (!promotion.stage || wantAtLeastReleaseStage(promotion.stage)) &&
        (promotion.id !== 'welcome-deposit-bonus' || !isLoggedIn || depositBonusOffersCount > 0) &&
        (promotion.id !== 'sports' || hasSports) &&
        (showExpiredPromotions ||
            !promotion.endDate ||
            new Date(promotion.endDate).getTime() >= now) &&
        (!promotion.requiresAuth || isLoggedIn) &&
        (promotion.enabled ?? true));
};
export const getPromotions = (featureSet, theme, locale, isLoggedIn) => {
    const now = Date.now();
    const list = getPromotionsByTheme(theme, locale, now);
    const depositBonusOffers = get(stateDepositBonusOffers);
    const hasSports = get(stateHasSports);
    const ui = get(stateUi);
    const showExpired = !!ui?.promotions?.showExpiredPromotions;
    const filtered = filterPromotionsByBusinessRules(list, {
        featureSet,
        isLoggedIn,
        hasSports: !!hasSports,
        depositBonusOffersCount: depositBonusOffers?.length ?? 0,
        showExpiredPromotions: showExpired,
        now,
        theme
    });
    return sortPromotions(filtered);
};
const promotionSortBucket = (promotion, now) => {
    if (!promotion.endDate)
        return 0;
    return promotion.endDate.getTime() >= now ? 1 : 2;
};
export const sortPromotions = (list) => {
    const now = Date.now();
    const normalizedList = list.map((promotion, originalIndex) => ({
        promotion: normalizePromotionDates(promotion),
        originalIndex
    }));
    return normalizedList
        .sort((a, b) => {
        const aExpired = promotionSortBucket(a.promotion, now) === 2;
        const bExpired = promotionSortBucket(b.promotion, now) === 2;
        if (aExpired !== bExpired)
            return aExpired ? 1 : -1;
        return a.originalIndex - b.originalIndex;
    })
        .map(({ promotion }) => promotion);
};
const toBannerFromCanonical = (entry, surfaceKey) => {
    const surface = entry[surfaceKey];
    if (!surface)
        return undefined;
    const promotion = entry.promotion;
    const banner = {
        ...surface,
        id: surface.id ?? promotion?.id ?? entry.id,
        url: surface.url ?? promotion?.url ?? '',
        show: promotion?.enabled ?? true,
        onlyImage: surface.onlyImage ?? promotion?.onlyImage,
        noSignUp: surface.noSignUp ?? promotion?.noSignUp,
        rel: surface.rel ?? promotion?.rel,
        stage: surface.stage ?? promotion?.stage
    };
    return banner;
};
const filterBanners = (data) => {
    const bannerFeatureSet = get(stateFeatureSet);
    return data.filter((banner) => banner.show &&
        (!banner.rel || banner.rel === bannerFeatureSet) &&
        (!banner.stage || wantAtLeastReleaseStage(banner.stage)));
};
export const getDataBanners = (lang) => {
    const theme = get(stateTheme);
    const entries = getCanonicalThemeEntries(theme, lang, Date.now());
    const banners = entries
        .filter((entry) => entry.casinoLobbyBanner)
        .sort((a, b) => (a.order?.casinoLobbyBanner ?? Number.MAX_SAFE_INTEGER) -
        (b.order?.casinoLobbyBanner ?? Number.MAX_SAFE_INTEGER))
        .map((entry) => toBannerFromCanonical(entry, 'casinoLobbyBanner'))
        .filter((banner) => !!banner);
    return filterBanners(banners);
};
export const getCasinoLobbyBanners = getDataBanners;
export const getDataBannersHome = (lang) => {
    const theme = get(stateTheme);
    const entries = getCanonicalThemeEntries(theme, lang, Date.now());
    const banners = entries
        .filter((entry) => entry.homePageBanner)
        .sort((a, b) => (a.order?.homePageBanner ?? Number.MAX_SAFE_INTEGER) -
        (b.order?.homePageBanner ?? Number.MAX_SAFE_INTEGER))
        .map((entry) => toBannerFromCanonical(entry, 'homePageBanner'))
        .filter((banner) => !!banner);
    return filterBanners(banners);
};
export const getHomePageBanners = getDataBannersHome;
const toTopSectionFromCanonical = (entry) => {
    const surface = entry.topSection;
    if (!surface)
        return undefined;
    const promotion = entry.promotion;
    const card = {
        ...surface,
        id: surface.id ?? promotion?.id ?? entry.id,
        backgroundImage: surface.backgroundImage ?? promotion?.banner ?? '',
        title: surface.title ?? promotion?.title ?? '',
        description: surface.description ?? promotion?.description ?? null,
        showTimezoneSuffixInFallback: surface.showTimezoneSuffixInFallback ?? promotion?.showTimezoneSuffixInFallback,
        badgeText: surface.badgeText,
        buttonText: surface.buttonText ?? promotion?.buttonText ?? '',
        link: surface.link ?? promotion?.url ?? '',
        amountBox: surface.amountBox ?? promotion?.amountBox,
        startDate: surface.startDate ?? promotion?.startDate ?? undefined,
        endDate: surface.endDate ?? promotion?.endDate ?? undefined,
        rel: surface.rel ?? promotion?.rel,
        conditionalLink: surface.conditionalLink ??
            (promotion?.conditionalUrl && {
                loggedInLink: promotion.conditionalUrl.loggedInUrl,
                loggedOutLink: promotion.conditionalUrl.loggedOutUrl
            })
    };
    return card;
};
export const flushCardsConfig = createFlushCanonicalItems()
    .map(normalizeCanonicalItem)
    .filter((entry) => entry.topSection)
    .sort((a, b) => (a.order?.topSection ?? Number.MAX_SAFE_INTEGER) -
    (b.order?.topSection ?? Number.MAX_SAFE_INTEGER))
    .map((entry) => toTopSectionFromCanonical(entry))
    .filter((card) => !!card);
export const fomoioCardsConfig = createFomoCanonicalItems('en', Date.now())
    .map(normalizeCanonicalItem)
    .filter((entry) => entry.topSection)
    .sort((a, b) => (a.order?.topSection ?? Number.MAX_SAFE_INTEGER) -
    (b.order?.topSection ?? Number.MAX_SAFE_INTEGER))
    .map((entry) => toTopSectionFromCanonical(entry))
    .filter((card) => !!card);
export const rocketbetCardsConfig = createRocketbetCanonicalItems()
    .map(normalizeCanonicalItem)
    .filter((entry) => entry.topSection)
    .sort((a, b) => (a.order?.topSection ?? Number.MAX_SAFE_INTEGER) -
    (b.order?.topSection ?? Number.MAX_SAFE_INTEGER))
    .map((entry) => toTopSectionFromCanonical(entry))
    .filter((card) => !!card);
function sameDateValue(a, b) {
    if (!a && !b)
        return true;
    if (!a || !b)
        return false;
    return a.getTime() === b.getTime();
}
function sameAmountBox(a, b) {
    if (!a && !b)
        return true;
    if (!a || !b)
        return false;
    return a.text === b.text && a.color === b.color && a.textColor === b.textColor;
}
function removePromotionDuplicatesFromBannerSurface(surface, promotion) {
    if (!surface || !promotion)
        return surface;
    const next = { ...surface };
    if (next.url === promotion.url)
        delete next.url;
    if (next.onlyImage === promotion.onlyImage)
        delete next.onlyImage;
    if (next.noSignUp === promotion.noSignUp)
        delete next.noSignUp;
    if (next.rel === promotion.rel)
        delete next.rel;
    if (next.stage === promotion.stage)
        delete next.stage;
    return next;
}
function removePromotionDuplicatesFromTopSectionSurface(surface, promotion) {
    if (!surface || !promotion)
        return surface;
    const next = { ...surface };
    if (next.title === promotion.title)
        delete next.title;
    if (next.description === promotion.description)
        delete next.description;
    if (next.showTimezoneSuffixInFallback === promotion.showTimezoneSuffixInFallback) {
        delete next.showTimezoneSuffixInFallback;
    }
    if (next.buttonText === promotion.buttonText)
        delete next.buttonText;
    if (next.link === promotion.url)
        delete next.link;
    if (next.rel === promotion.rel)
        delete next.rel;
    if (next.backgroundImage === promotion.banner)
        delete next.backgroundImage;
    if (sameAmountBox(next.amountBox, promotion.amountBox))
        delete next.amountBox;
    if (sameDateValue(next.startDate, promotion.startDate))
        delete next.startDate;
    if (sameDateValue(next.endDate, promotion.endDate))
        delete next.endDate;
    if (next.conditionalLink?.loggedInLink === promotion.conditionalUrl?.loggedInUrl &&
        next.conditionalLink?.loggedOutLink === promotion.conditionalUrl?.loggedOutUrl) {
        delete next.conditionalLink;
    }
    return next;
}
function normalizeCanonicalItem(item) {
    const { surfaces, ...rest } = item;
    const promotion = item.promotion;
    const casinoLobbyBanner = removePromotionDuplicatesFromBannerSurface(item.casinoLobbyBanner ?? item.surfaces?.bannerSwiper, promotion);
    const homePageBanner = removePromotionDuplicatesFromBannerSurface(item.homePageBanner ?? item.surfaces?.bannerSwiperHome, promotion);
    const topSection = removePromotionDuplicatesFromTopSectionSurface(item.topSection ?? item.surfaces?.topSection, promotion);
    return {
        ...rest,
        casinoLobbyBanner,
        homePageBanner,
        topSection,
        order: {
            ...item.order,
            casinoLobbyBanner: item.order?.casinoLobbyBanner ?? item.order?.bannerSwiper,
            homePageBanner: item.order?.homePageBanner ?? item.order?.bannerSwiperHome
        }
    };
}
const mergeCanonicalThemeEntries = ({ promotions, banners, bannerHomes, topSectionCards }) => {
    const byId = new Map();
    const ensure = (id) => {
        let entry = byId.get(id);
        if (!entry) {
            entry = { id, order: {} };
            byId.set(id, entry);
        }
        return entry;
    };
    promotions.forEach((promotion, index) => {
        const entry = ensure(promotion.id);
        entry.promotion = promotion;
        entry.order = { ...entry.order, promotions: index };
    });
    banners.forEach((banner, index) => {
        const entry = ensure(banner.id);
        entry.casinoLobbyBanner = banner;
        entry.order = { ...entry.order, casinoLobbyBanner: index };
    });
    bannerHomes.forEach((banner, index) => {
        const entry = ensure(banner.id);
        entry.homePageBanner = banner;
        entry.order = { ...entry.order, homePageBanner: index };
    });
    topSectionCards.forEach((card, index) => {
        const entry = ensure(card.id);
        entry.topSection = removePromotionDuplicatesFromTopSectionSurface(card, entry.promotion);
        entry.order = { ...entry.order, topSection: index };
    });
    return Array.from(byId.values());
};
export const getCanonicalByTheme = (locale, now) => {
    const canonicalByTheme = {
        [Theme.Flush]: createFlushCanonicalItems(),
        [Theme.Fomo]: createFomoCanonicalItems(locale, now),
        [Theme.Rocketbet]: createRocketbetCanonicalItems(),
        [Theme.Luna]: createLunaCanonicalItems(),
        [Theme.Turboslots]: createTurboCanonicalItems(),
        [Theme.Dynamic]: createDynamicCanonicalItems(),
        // Wild Ape rides on the same catalog as Flush — no dedicated promotions yet.
        [Theme.WildApe]: createFlushCanonicalItems()
    };
    return {
        [Theme.Flush]: canonicalByTheme[Theme.Flush].map(normalizeCanonicalItem),
        [Theme.Fomo]: canonicalByTheme[Theme.Fomo].map(normalizeCanonicalItem),
        [Theme.Rocketbet]: canonicalByTheme[Theme.Rocketbet].map(normalizeCanonicalItem),
        [Theme.Luna]: canonicalByTheme[Theme.Luna].map(normalizeCanonicalItem),
        [Theme.Turboslots]: canonicalByTheme[Theme.Turboslots].map(normalizeCanonicalItem),
        [Theme.Dynamic]: canonicalByTheme[Theme.Dynamic].map(normalizeCanonicalItem),
        [Theme.WildApe]: canonicalByTheme[Theme.WildApe].map(normalizeCanonicalItem)
    };
};
const getCanonicalThemeEntries = (theme, locale, now) => {
    const canonicalByTheme = getCanonicalByTheme(locale, now);
    return canonicalByTheme[theme] ?? [];
};
export const getFilteredCards = (featureSet, depositBonusOffersAvailable = false, races, loggedIn, theme) => {
    const now = Date.now();
    const ignoreDates = featureSet === FeatureSet.Preview;
    const hasRaces = Array.isArray(races) && races.length > 0;
    const cardsToFilter = getCanonicalThemeEntries(theme ?? Theme.Flush, 'en', now)
        .filter((entry) => entry.topSection)
        .sort((a, b) => (a.order?.topSection ?? Number.MAX_SAFE_INTEGER) -
        (b.order?.topSection ?? Number.MAX_SAFE_INTEGER))
        .map((entry) => toTopSectionFromCanonical(entry))
        .filter((card) => !!card);
    return cardsToFilter.filter((card) => {
        const cardFeatureSet = card.rel ?? card.featureSet;
        if (cardFeatureSet && cardFeatureSet !== featureSet) {
            return false;
        }
        if (!ignoreDates) {
            if (card.startDate && now < card.startDate.getTime())
                return false;
            if (card.endDate && now > card.endDate.getTime())
                return false;
        }
        if (card.requiresDepositBonus && !depositBonusOffersAvailable) {
            return false;
        }
        if (card.id === 'flush-races' && !hasRaces && !ignoreDates) {
            return false;
        }
        if (card.onlyAuthed && !loggedIn) {
            return false;
        }
        if (card.requiresSports && !cfgHasSports()) {
            return false;
        }
        return true;
    });
};
export const getTopSectionCards = getFilteredCards;
export { resolvePromotionDescriptionDisplay } from './promotion-description-fallback';
